import { CaretDownOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Input,
  Form,
  Flex,
  Typography,
  DatePicker,
  Checkbox,
  Dropdown,
  Button,
  Divider,
} from 'antd';
import { useMemo, type FC } from 'react';
import { useLocation } from 'react-router-dom';

import { SearchButtons } from '@app/components';
import {
  DATE_PICKER_FORMAT,
  MIN_THREE_SYMBOLS,
  PREFIX,
  rangePickerLocale,
  today,
  weekBeforeToday,
} from '@app/constants';
import type { LogsRegistryFiltersProps } from '@app/pages/SalesFunnels/LogsRegistry/types';
import { divideAmount } from '@app/utils';

import type { MenuProps } from 'antd';

import '@app/pages/SalesFunnels/LogsRegistry/styles/LogsRegistryFilters.desktop.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const LogsRegistryFilters: FC<LogsRegistryFiltersProps> = ({
  resetFilters,
  filtersForm,
  handleSubmitFilterForm,
  selectionCount,
  totalRowsCount,
  openMarkAsReadModal,
}) => {
  const location = useLocation();

  const salesFunnelId = location.state?.salesFunnelId;
  const robotId = location.state?.robotId;

  const MassActionButton = useMemo(() => {
    const items: MenuProps['items'] = [
      {
        key: '1',
        label: (
          <span onClick={() => openMarkAsReadModal(1)}>Прочитать все логи</span>
        ),
      },
      {
        key: '2',
        label: (
          <span onClick={() => openMarkAsReadModal(2)}>
            {`Прочитать выбранные логи (${selectionCount})`}
          </span>
        ),
        disabled: selectionCount === 0,
      },
      {
        key: '3',
        label: (
          <span onClick={() => openMarkAsReadModal(3)}>
            Прочитать все логи по фильтру
          </span>
        ),
      },
    ];

    return (
      <div className="mass-actions">
        <Dropdown placement="bottomLeft" menu={{ items }}>
          <Button type="primary">
            Действия
            <CaretDownOutlined />
          </Button>
        </Dropdown>
      </div>
    );
  }, [openMarkAsReadModal, selectionCount]);

  return (
    <div className={`${PREFIX}organization-users-filters`}>
      <Form
        form={filtersForm}
        onFinish={handleSubmitFilterForm}
        className="filters-form"
        initialValues={{
          Date: [weekBeforeToday, today],
          LeadIds: null,
          SalesFunnelIds: salesFunnelId,
          RobotIds: robotId,
        }}
      >
        <Flex vertical gap={16}>
          <Flex vertical gap={10}>
            <Text className="label" strong>
              Период:
            </Text>
            <Form.Item name="Date">
              <RangePicker
                format={DATE_PICKER_FORMAT}
                size="middle"
                allowEmpty={[true, true]}
                locale={rangePickerLocale}
              />
            </Form.Item>
          </Flex>

          <Form.Item
            name="SearchTerm"
            wrapperCol={{ span: 16 }}
            rules={[
              {
                min: 3,
                message: MIN_THREE_SYMBOLS,
              },
            ]}
          >
            <Input
              placeholder="Введите текст сообщения"
              addonBefore={<SearchOutlined />}
            />
          </Form.Item>
          <Flex justify="space-between" wrap gap={16}>
            <Flex gap={16} wrap align="center">
              <Flex gap={10} align="center">
                <Text type="secondary">ID воронки:</Text>
                <Form.Item
                  name="SalesFunnelIds"
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: 'Допустимы только цифры',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Flex>
              {robotId && (
                <Flex gap={10} align="center">
                  <Text type="secondary">ID робота:</Text>
                  <Form.Item
                    name="RobotIds"
                    rules={[
                      {
                        pattern: /^\d+$/,
                        message: 'Допустимы только цифры',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Flex>
              )}
              <Flex gap={10} align="center">
                <Text type="secondary">ID лида:</Text>
                <Form.Item
                  name="LeadIds"
                  rules={[
                    {
                      pattern: /^\d+$/,
                      message: 'Допустимы только цифры',
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Flex>
              <Form.Item name="IsUnread" valuePropName="checked">
                <Checkbox>Непрочитанные</Checkbox>
              </Form.Item>
            </Flex>
            <SearchButtons
              className="tablet-buttons"
              resetAll={resetFilters}
              showData={filtersForm.submit}
            />
          </Flex>

          <Divider className="mg-0" />

          <Flex justify="space-between" wrap gap={16}>
            <Text className="records-count" type="secondary">
              {`Выбрано ${divideAmount(selectionCount)} из ${divideAmount(
                totalRowsCount
              )} логов`}
            </Text>
            {MassActionButton}
          </Flex>
        </Flex>
      </Form>
    </div>
  );
};
